/**
 * Portfolio page load-order gate — Round P124c (Feb 2026).
 *
 * PROBLEM being solved:
 * The portfolio page has ~30 components that each fire their own
 * fetches on mount. Browsers cap concurrent HTTP requests at 6, so
 * when everything fires simultaneously the slow calls (benchmark
 * 1.6 s, value-history 1.2 s) starve the fast-and-most-important
 * one (portfolio summary, ~200 ms).
 *
 * FIRST ATTEMPT (P124) — event-chain gating.
 * Each stage waited for the PREVIOUS stage's response to arrive
 * before firing. On paper this was elegant; in practice it created
 * a 12-second cascade because the summary response itself was
 * queued behind un-gated fetches from OTHER components (paycheck,
 * warm-triangles, dividend-alerts, benchmark from a peer card...).
 *
 * CURRENT (P124c) — time-based stagger.
 * Instead of waiting on responses, we release each stage on a
 * fixed timer relative to mount. This guarantees the summary
 * request enters the browser's HTTP queue FIRST (t=0) — which is
 * all we need to win the "who renders first" race — without
 * blocking later stages if the summary response happens to be
 * slow. The offsets are small enough (max 800 ms) that even in
 * the worst case the whole staircase is done in under a second.
 *
 *   Stage 1: Summary (fires at t=0, no gate)
 *   Stage 2: Capital Tracker (fires at t + 120 ms)
 *   Stage 3: Sector Allocation (fires at t + 240 ms)
 *   Stage 4: Middle-of-page fetches (fires at t + 480 ms)
 *   Stage 5: Portfolio Value chart (fires at t + 900 ms)
 *
 * `PortfolioSummary` kicks off the staircase on mount via
 * `startPortfolioLoadStaircase()`. Each subscribing component
 * gates its own fetch on `usePortfolioLoadStage() >= N`.
 */
import { useEffect, useState } from 'react';

let currentStage = 1;
const listeners = new Set();
let staircaseTimers = [];

function notify() {
  for (const cb of listeners) {
    try { cb(currentStage); } catch { /* swallow listener errors */ }
  }
}

export function advancePortfolioLoadStage(n) {
  const next = Math.max(currentStage, Number(n) || 0);
  if (next === currentStage) return;
  currentStage = next;
  notify();
}

export function getPortfolioLoadStage() {
  return currentStage;
}

/**
 * Fire the release timers. Idempotent — repeat calls (e.g. from
 * strict-mode double-invoke or portfolio switches) clear any
 * pending timers first, then reschedule from t=0. If we're
 * already at stage 5 nothing happens.
 */
export function startPortfolioLoadStaircase() {
  if (currentStage >= 5) return;
  // Clear any in-flight timers so we don't stack duplicates.
  staircaseTimers.forEach(clearTimeout);
  staircaseTimers = [];
  // Sep 2026 — called AFTER the live headline has landed, so stage 2
  // (Capital Tracker) releases immediately and the rest follows.
  const schedule = [
    { at: 0, stage: 2 },
    { at: 150, stage: 3 },
    { at: 400, stage: 4 },
    { at: 800, stage: 5 },
  ];
  for (const { at, stage } of schedule) {
    staircaseTimers.push(setTimeout(() => advancePortfolioLoadStage(stage), at));
  }
}

export function usePortfolioLoadStage() {
  const [stage, setStage] = useState(currentStage);
  useEffect(() => {
    const cb = (s) => setStage(s);
    listeners.add(cb);
    // Catch any advance that happened between initial render and
    // effect registration.
    if (currentStage !== stage) setStage(currentStage);
    return () => { listeners.delete(cb); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return stage;
}
